import React, { useState } from 'react';
import {
  BarChart3,
  Play,
  Download,
  Eye,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  Image as ImageIcon
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ChangeDetectionJob {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  metrics: {
    changed_area: number;
    change_percentage: number;
    total_area: number;
    change_types?: Array<{
      type: string;
      area: number;
      percentage: number;
    }>;
  };
  preview_url?: string;
  geotiff_url?: string;
  created_at: string;
  completed_at?: string;
  before_date?: string;
  after_date?: string;
}

interface EnhancedChangeDetectionProps {
  beforeRelease?: any;
  afterRelease?: any;
  aoi?: any;
  onJobComplete?: (job: ChangeDetectionJob) => void;
  onBeforeSelect?: (releaseId: string | number | null) => void;
  onAfterSelect?: (releaseId: string | number | null) => void;
  className?: string;
}

const EnhancedChangeDetection: React.FC<EnhancedChangeDetectionProps> = ({
  beforeRelease,
  afterRelease,
  aoi,
  onJobComplete,
  onBeforeSelect,
  onAfterSelect,
  className = ''
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [jobs, setJobs] = useState<ChangeDetectionJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<ChangeDetectionJob | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [method, setMethod] = useState('ndvi');
  const [threshold, setThreshold] = useState(0.1);
  const [sensitivity, setSensitivity] = useState('medium');
  const { toast } = useToast();

  const CHANGE_TYPE_LABELS: Record<string, { label: string; color: string; icon: string }> = {
    construction: { label: 'Construction / Soil', color: 'text-orange-400', icon: '🏗' },
    vegetation: { label: 'Vegetation Change', color: 'text-green-400', icon: '🌿' },
    vegetation_loss: { label: 'Vegetation Loss', color: 'text-red-400', icon: '🍂' },
    water: { label: 'Water Change', color: 'text-blue-400', icon: '💧' },
    urban: { label: 'Urban / General', color: 'text-gray-400', icon: '🏙' },
    mixed: { label: 'Mixed Changes', color: 'text-purple-400', icon: '🔄' },
    minimal: { label: 'Minimal Change', color: 'text-muted-foreground', icon: '—' },
  };

  const runChangeDetection = async () => {
    if (!beforeRelease || !afterRelease || !aoi) {
      alert('Please select before/after releases and define an AOI');
      return;
    }
    
    console.log('Change detection - beforeRelease:', beforeRelease);
    console.log('Change detection - afterRelease:', afterRelease);
    console.log('Change detection - aoi:', aoi);

    setIsRunning(true);
    try {
      // Extract geometry from AOI - API expects GeoJSON geometry
      let aoiGeometry = aoi.geometry || aoi;
      
      // Ensure it's a proper GeoJSON geometry object
      if (typeof aoiGeometry === 'object' && aoiGeometry !== null) {
        // If it has coordinates but no type, assume Polygon
        if (aoiGeometry.coordinates && !aoiGeometry.type) {
          aoiGeometry = {
            type: 'Polygon',
            coordinates: aoiGeometry.coordinates
          };
        }
      }
      
      // Include the AOI's document ID in the geometry so the backend can link
      // change detection events to the correct AOI entity
      const aoiId = aoi?.id || aoi?.aoi_id;
      const requestBody = {
        aoi: aoiId ? { ...aoiGeometry, id: aoiId } : aoiGeometry,
        before_release_id: beforeRelease.id,
        after_release_id: afterRelease.id,
        method: method,
        threshold: threshold,
        sensitivity: sensitivity
      };
      
      console.log('Change detection request:', requestBody);
      
      const response = await fetch('/api/geoint/wayback/change-detection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Change detection failed:', response.status, errorText);
        throw new Error(`Change detection failed: ${response.status} - ${errorText}`);
      }

      const job = await response.json();
      setJobs(prev => [...prev, job]);
      
      pollJobStatus(job.job_id);
    } catch (error) {
      console.error('Change detection error:', error);
      toast({
        title: 'Change detection failed',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsRunning(false);
    }
  };

  const pollJobStatus = (jobId: string) => {
    // Give up only after a few consecutive failed status checks in a row —
    // a single transient network hiccup used to permanently kill this
    // interval, leaving the job stuck showing a spinner forever with no
    // way to tell the user anything had gone wrong.
    let consecutiveFailures = 0;
    const MAX_CONSECUTIVE_FAILURES = 3;

    const giveUp = () => {
      clearInterval(pollInterval);
      setJobs(prev =>
        prev.map(j => j.job_id === jobId ? { ...j, status: 'failed' as const } : j)
      );
      toast({
        title: 'Lost connection to change detection job',
        description: 'Could not get a status update after several attempts. The job may still be running on the server.',
        variant: 'destructive',
      });
    };

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`/api/geoint/wayback/change-detection/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token')}`
          }
        });
        if (response.ok) {
          consecutiveFailures = 0;
          const job = await response.json();
          setJobs(prev =>
            prev.map(j => j.job_id === jobId ? job : j)
          );

          if (job.status === 'completed' || job.status === 'failed') {
            clearInterval(pollInterval);
            if (job.status === 'completed' && onJobComplete) {
              onJobComplete(job);
            }
          }
        } else {
          consecutiveFailures += 1;
          console.error('Status polling error: unexpected response', response.status);
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            giveUp();
          }
        }
      } catch (error) {
        consecutiveFailures += 1;
        console.error('Status polling error:', error);
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          giveUp();
        }
      }
    }, 5000);

    setTimeout(() => clearInterval(pollInterval), 600000);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Enhanced Change Detection Parameters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Change Detection Analysis
        </h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Detection Method
              </label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="ndvi">NDVI (Vegetation)</option>
                <option value="ndwi">NDWI (Water)</option>
                <option value="spectral_diff">Spectral Difference</option>
                <option value="multispectral">Multi-spectral</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Change Threshold
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Sensitivity
              </label>
              <select
                value={sensitivity}
                onChange={(e) => setSensitivity(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Before</p>
              <p className="font-medium">
                {beforeRelease ? `${new Date(beforeRelease.date).toLocaleDateString()}` : 'Not selected'}
              </p>
              {onBeforeSelect && (
                <button
                  onClick={() => onBeforeSelect(null)}
                  className="mt-2 text-xs text-primary hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">After</p>
              <p className="font-medium">
                {afterRelease ? `${new Date(afterRelease.date).toLocaleDateString()}` : 'Not selected'}
              </p>
              {onAfterSelect && (
                <button
                  onClick={() => onAfterSelect(null)}
                  className="mt-2 text-xs text-primary hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <button 
            onClick={runChangeDetection}
            disabled={isRunning || !beforeRelease || !afterRelease || !aoi}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground text-primary-foreground rounded-md text-sm font-medium transition-colors"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Running Analysis...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run Change Detection
              </>
            )}
          </button>
        </div>
      </div>

      {/* Enhanced Results Display */}
      {jobs.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Detection Results ({jobs.length})
          </h3>
          <div className="space-y-3">
            {jobs.map((job) => (
              <div
                key={job.job_id}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedJob?.job_id === job.job_id 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setSelectedJob(job)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(job.status)}
                    <span className="font-medium">Job {job.job_id.slice(0, 8)}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      job.status === 'completed' ? 'bg-green-100 text-green-700' :
                      job.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {job.status}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(job.created_at).toLocaleString()}
                  </div>
                </div>

                {job.status === 'completed' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-muted rounded">
                        <TrendingUp className="w-6 h-6 mx-auto mb-1 text-primary" />
                        <p className="text-2xl font-bold text-primary">
                          {job.metrics.changed_area.toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">Changed Area (km²)</p>
                      </div>
                      <div className="text-center p-3 bg-muted rounded">
                        <BarChart3 className="w-6 h-6 mx-auto mb-1 text-primary" />
                        <p className="text-2xl font-bold text-primary">
                          {job.metrics.change_percentage.toFixed(2)}%
                        </p>
                        <p className="text-xs text-muted-foreground">Change %</p>
                      </div>
                      <div className="text-center p-3 bg-muted rounded">
                        <ImageIcon className="w-6 h-6 mx-auto mb-1 text-primary" />
                        <p className="text-2xl font-bold text-primary">
                          {job.metrics.total_area.toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">Total Area (km²)</p>
                      </div>
                    </div>
                    
                    {job.preview_url && (
                      <div className="aspect-video bg-muted rounded overflow-hidden">
                        <img
                          src={job.preview_url}
                          alt="Change detection result"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    <div className="flex gap-2">
                      {job.geotiff_url && (
                        <a
                          href={job.geotiff_url}
                          download
                          className="flex items-center gap-2 px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-md text-sm font-medium transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download GeoTIFF
                        </a>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedJobId(expandedJobId === job.job_id ? null : job.job_id);
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md text-sm font-medium transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        {expandedJobId === job.job_id ? 'Hide Details' : 'View Details'}
                      </button>
                    </div>

                    {/* Expanded detail panel */}
                    {expandedJobId === job.job_id && (
                      <div className="mt-3 p-3 bg-muted/50 rounded-lg border border-border space-y-3">
                        {/* Change Type Classification */}
                        {job.metrics.change_types && job.metrics.change_types.length > 0 ? (
                          <div>
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                              Detected Change Types
                            </h4>
                            <div className="space-y-1.5">
                              {job.metrics.change_types.map((ct, i) => {
                                const meta = CHANGE_TYPE_LABELS[ct.type] || { label: ct.type, color: 'text-foreground', icon: '?' };
                                return (
                                  <div key={i} className="flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-2">
                                      <span>{meta.icon}</span>
                                      <span className={`font-medium ${meta.color}`}>{meta.label}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-muted-foreground">{ct.percentage.toFixed(1)}%</span>
                                      <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                                        <div
                                          className="h-full bg-primary rounded-full"
                                          style={{ width: `${Math.min(ct.percentage, 100)}%` }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">No change classification data available.</p>
                        )}

                        {/* Analysis Summary */}
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                            Analysis Summary
                          </h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Method:</span>
                              <span className="font-medium">{(job.metrics as any).method?.toUpperCase() || method.toUpperCase()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Sensitivity:</span>
                              <span className="font-medium capitalize">{(job.metrics as any).sensitivity || sensitivity}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Tiles analyzed:</span>
                              <span className="font-medium">{(job.metrics as any).tiles_analyzed || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Tiles changed:</span>
                              <span className="font-medium">{(job.metrics as any).tiles_changed || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Mean magnitude:</span>
                              <span className="font-medium">{(job.metrics as any).mean_change_magnitude?.toFixed(4) || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Max magnitude:</span>
                              <span className="font-medium">{(job.metrics as any).max_change_magnitude?.toFixed(4) || '—'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Interpretation */}
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                            Interpretation
                          </h4>
                          <p className="text-xs text-foreground leading-relaxed">
                            {job.metrics.change_percentage > 50
                              ? `Significant changes detected across ${job.metrics.change_percentage.toFixed(1)}% of the AOI (${job.metrics.changed_area.toFixed(2)} km²). `
                              : job.metrics.change_percentage > 10
                              ? `Moderate changes detected in ${job.metrics.change_percentage.toFixed(1)}% of the AOI. `
                              : `Minor changes detected in ${job.metrics.change_percentage.toFixed(1)}% of the AOI. `}
                            {job.metrics.change_types?.[0]?.type === 'construction'
                              ? 'Primary activity appears to be construction or ground disturbance.'
                              : job.metrics.change_types?.[0]?.type === 'vegetation'
                              ? 'Primary changes are in vegetation cover — possible clearing, growth, or seasonal shift.'
                              : job.metrics.change_types?.[0]?.type === 'vegetation_loss'
                              ? 'Vegetation loss detected — possible deforestation, land clearing, or drought impact.'
                              : job.metrics.change_types?.[0]?.type === 'water'
                              ? 'Water body changes detected — possible flooding, drainage, or reservoir level change.'
                              : 'Mixed or general changes across multiple categories.'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedChangeDetection;


